import { useMediaQuery } from "@mui/material";
import { FC, useCallback, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
    GraduationCapIcon,
    MemberIcon,
    SettingsIcon,
    TradesIcon,
} from "../../assets/img/icons";
import Button from "../../components/Button";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import Journal from "./Journal";
import Progression from "./Progression";
import Tags from "./Tags";

import styles from "./styles.module.scss";

const Settings: FC = () => {
    const { hasPermission } = useContext(AuthContext)!;
    const canReadSettings = hasPermission("admin-settings", "read");
    const { requestNavigate } = useUnsavedChanges();

    const tabs = useMemo(() => {
        const items = [{ label: "Tags", key: "", icon: MemberIcon }];
        if (canReadSettings) {
            items.push({
                label: "Progression",
                key: "progression",
                icon: GraduationCapIcon,
            });
            items.push({
                label: "Journal",
                key: "journal",
                icon: TradesIcon,
            });
        }
        return items;
    }, [canReadSettings]);

    const location = useLocation();
    const isMobile = useMediaQuery("(max-width: 1050px)");

    const activeTab = useMemo(() => {
        const path = location.pathname.split("/").pop();
        if (path === "journal" && canReadSettings) return "journal";
        // Compat ancien chemin /niveau
        if (
            (path === "progression" || path === "niveau") &&
            canReadSettings
        ) {
            return "progression";
        }
        return "";
    }, [location.pathname, canReadSettings]);

    const handleTabChange = useCallback(
        (key: string) => {
            requestNavigate(`/admin/settings/${key}`);
        },
        [requestNavigate]
    );

    const renderTabContent = useMemo(() => {
        switch (activeTab) {
            case "progression":
                return <Progression handleTabChange={handleTabChange} />;
            case "journal":
                return <Journal handleTabChange={handleTabChange} />;
            case "":
                return <Tags handleTabChange={handleTabChange} />;
            default:
                return null;
        }
    }, [activeTab, handleTabChange]);

    return (
        <ConnectedLayout>
            <Header icon={SettingsIcon} title="Paramètres" />
            <div className={styles.account_container}>
                {!isMobile && (
                    <div className={styles.account_tabs}>
                        {tabs.map((tab) => (
                            <Button
                                key={tab.key}
                                variant={
                                    activeTab === tab.key
                                        ? "secondary"
                                        : "transparent"
                                }
                                size="medium"
                                Icon={tab.icon}
                                labelOptions={{ align: "left" }}
                                className={`account_tab`}
                                onClick={() => handleTabChange(tab.key)}
                                label={tab.label}
                            />
                        ))}
                    </div>
                )}
                {!isMobile && <div className={styles.account_divider} />}
                <div className={styles.account_tab_content}>{renderTabContent}</div>
            </div>
        </ConnectedLayout>
    );
};

export default Settings;
